import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createGenealogyTheme, ThemeVariant } from '../theme';
import { systemConfigService } from '../services/systemConfigService';

type PaletteMode = 'light' | 'dark';

interface ThemeContextType {
  mode: PaletteMode;
  variant: ThemeVariant;
  toggleTheme: () => void;
  setThemeMode: (mode: PaletteMode) => void;
  setThemeVariant: (variant: ThemeVariant) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);

  // Initialize theme mode from localStorage, global defaults, or system preference
  const [mode, setMode] = useState<PaletteMode>('light');

  // Initialize theme variant from localStorage or global defaults
  const [variant, setVariant] = useState<ThemeVariant>('heritage');

  // Load theme preferences on mount
  useEffect(() => {
    const loadThemePreferences = async () => {
      try {
        // Check user's local preferences first
        const savedMode = localStorage.getItem('themeMode') as PaletteMode;
        const savedVariant = localStorage.getItem('themeVariant') as ThemeVariant;

        let finalMode: PaletteMode;
        let finalVariant: ThemeVariant;

        // If user has local preferences, use them
        if (savedMode === 'light' || savedMode === 'dark') {
          finalMode = savedMode;
        } else {
          // Otherwise, load global defaults from systemConfig
          try {
            const config = await systemConfigService.getConfig();
            if (config && config.lookAndFeel) {
              finalMode = config.lookAndFeel.defaultThemeMode;
            } else {
              // Fall back to system preference
              if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
                finalMode = 'dark';
              } else {
                finalMode = 'light';
              }
            }
          } catch (error) {
            // Fall back to system preference on error
            if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
              finalMode = 'dark';
            } else {
              finalMode = 'light';
            }
          }
        }

        // Load variant preference
        if (
          savedVariant === 'heritage' ||
          savedVariant === 'blue' ||
          savedVariant === 'green' ||
          savedVariant === 'orange' ||
          savedVariant === 'purple' ||
          savedVariant === 'christmas'
        ) {
          finalVariant = savedVariant;
        } else {
          // Load global default variant from systemConfig
          try {
            const config = await systemConfigService.getConfig();
            finalVariant = config?.lookAndFeel?.defaultThemeVariant || 'heritage';
          } catch (error) {
            finalVariant = 'heritage';
          }
        }

        setMode(finalMode);
        setVariant(finalVariant);
      } catch (error) {
        console.error('Error loading theme preferences:', error);
        // Use defaults on error
        setMode('light');
        setVariant('heritage');
      } finally {
        setInitialized(true);
      }
    };

    loadThemePreferences();
  }, []);

  // Save theme preferences to localStorage (marks as user preference)
  useEffect(() => {
    if (initialized) {
      localStorage.setItem('themeMode', mode);
    }
  }, [mode, initialized]);

  useEffect(() => {
    if (initialized) {
      localStorage.setItem('themeVariant', variant);
    }
  }, [variant, initialized]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const setThemeMode = (newMode: PaletteMode) => {
    setMode(newMode);
  };

  const setThemeVariant = (newVariant: ThemeVariant) => {
    setVariant(newVariant);
  };

  const theme = createGenealogyTheme(mode, variant);

  // Don't render until theme is initialized to prevent flash
  if (!initialized) {
    return null;
  }

  return (
    <ThemeContext.Provider value={{ mode, variant, toggleTheme, setThemeMode, setThemeVariant }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;
